import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { CalendarDays } from "lucide-react";
import Calendar from "../../../Ui_Components/Calendar";

interface DOBPickerProps {
  value: string; // "DD/MM/YYYY" or ""
  onChange: (value: string) => void;
  error?: string;
  maxDate?: Date;
}

const formatDate = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const parseDate = (str: string): Date | null => {
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map(Number);
  if (!dd || !mm || !yyyy || yyyy < 1900) return null;
  const d = new Date(yyyy, mm - 1, dd);
  return isNaN(d.getTime()) ? null : d;
};

const DOBPicker: React.FC<DOBPickerProps> = ({ value, onChange, error, maxDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDate = parseDate(value) ?? today;

  /* Close on outside click */
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const inTrigger = triggerRef.current?.contains(e.target as Node);
      const inCalendar = calendarRef.current?.contains(e.target as Node);
      if (!inTrigger && !inCalendar) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  /* Smart positioning */
  const rect = triggerRef.current?.getBoundingClientRect();
  const calendarHeight = 320;
  const openUpward =
    rect !== undefined &&
    window.innerHeight - rect.bottom < calendarHeight + 8 &&
    rect.top > window.innerHeight - rect.bottom;

  const portalStyle: React.CSSProperties = rect
    ? openUpward
      ? {
          position: "fixed",
          bottom: window.innerHeight - rect.top + 4,
          left: rect.left,
          zIndex: 9999,
        }
      : {
          position: "fixed",
          top: rect.bottom + 4,
          left: rect.left,
          zIndex: 9999,
        }
    : {};

  const handleDateSelect = (date: Date) => {
    onChange(formatDate(date));
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={`w-full px-3 py-2 border rounded-md text-sm 3xl:text-base font-inter text-left flex items-center justify-between transition-colors bg-white ${
          error
            ? "border-red-400"
            : isOpen
            ? "border-[#0DB5B5]"
            : "border-[#E0E0E0]"
        }`}
      >
        <span className={value ? "text-[#1F1F1F]" : "text-[#9E9E9E]"}>
          {value || "DD/MM/YYYY"}
        </span>
        <CalendarDays className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
      </button>
      {error && (
        <p className="mt-1.5 text-xs 3xl:text-sm text-red-500 font-inter">{error}</p>
      )}

      {isOpen &&
        rect &&
        createPortal(
          <div ref={calendarRef} style={portalStyle}>
            <Calendar
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              maxDate={maxDate}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default DOBPicker;
